import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhFileMonitoringApi } from 'src/api/mindtime/wazuhFileMonitoringApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhFimEndpointItem,
  WazuhFimEventItem,
  WazuhFimEventsQuery,
  WazuhFimSummaryData,
} from 'src/types/mindtime/wazuhFileMonitoring';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type FileMonitoringFilters = {
  changeType?: string;
  severityBand?: string;
  ruleLevel?: string;
  wazuhAgentId?: string;
  organizationId?: string;
  user?: string;
};

export type FileMonitoringState = {
  loading: boolean;
  summary: WazuhFimSummaryData | null;
  items: WazuhPagedData<WazuhFimEventItem> | null;
  endpoints: WazuhPagedData<WazuhFimEndpointItem> | null;
};

type BuildFileMonitoringQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: FileMonitoringFilters;
  page?: number;
  pageSize?: number;
};

const emptyState = (): FileMonitoringState => ({
  loading: true,
  summary: null,
  items: null,
  endpoints: null,
});

const buildFileMonitoringQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  selectedEndpointId,
  page,
  pageSize,
}: BuildFileMonitoringQueryInput & { selectedEndpointId?: string | null }): WazuhFimEventsQuery => {
  const { changeType, severityBand, ruleLevel, organizationId, user } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(changeType ? { changeType } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(ruleLevel ? { ruleLevel } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(user ? { user } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useFileMonitoringExport = (
  search = '',
  filters: FileMonitoringFilters = {}
) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('file-integrity-monitoring');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhFileMonitoringApi
      .export(
        buildFileMonitoringQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'file_monitoring_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useFileMonitoringExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useFileMonitoringData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: FileMonitoringFilters = {}
): FileMonitoringState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('file-integrity-monitoring');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'file-integrity-monitoring');
  const canSummary = canWidget('summary');
  const canEvents = canWidget('events');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<FileMonitoringState>(emptyState);

  const { changeType, severityBand, ruleLevel, organizationId, user } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as FileMonitoringState);
      return undefined;
    }

    if (!canSummary) {
      setState((prev) => ({ ...prev, summary: null }));
      return undefined;
    }

    let cancelled = false;

    mindtimeWazuhFileMonitoringApi
      .getSummary(
        buildEdrApiScope({
          scope,
          profileOrgId,
          isReseller,
          pageOrganizationId: organizationId || undefined,
        })
      )
      .then((summary) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useFileMonitoringData.summary');
          setState((prev) => ({ ...prev, summary: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId, isPreviewMode, wait, getPreviewState, canSummary]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canEvents) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhFileMonitoringApi
      .getEvents(
        buildFileMonitoringQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
          selectedEndpointId,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useFileMonitoringData.events');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    changeType,
    severityBand,
    ruleLevel,
    wazuhAgentId,
    organizationId,
    user,
    isPreviewMode,
    skipLive,
    canEvents,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canAffectedEndpoints) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;

    mindtimeWazuhFileMonitoringApi
      .listEndpoints(
        buildFileMonitoringQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page: 0,
          pageSize,
          selectedEndpointId,
        })
      )
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useFileMonitoringData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    pageSize,
    search,
    changeType,
    severityBand,
    ruleLevel,
    wazuhAgentId,
    organizationId,
    user,
    isPreviewMode,
    skipLive,
    canAffectedEndpoints,
  ]);

  return state;
};
